import "./filtered-result-list.styles.scss";
import { useContext, useMemo, useState } from "react";
import InsuranceOffer from "../insurance-offer/insurance-offer.component";
import PlanHighlights from "../plan-highlights/plan-highlights.component";
import SearchProgress from "../search-progress/search-progress.component";
import CompareTray from "../compare-tray/compare-tray.component";
import { CompareContext } from "../../context/compare.context";
import DropMenu from "../drop-menu/drop-menu.component";
import FilteringDropMenu from "../filtering-drop-menu/filtering-drop-menu.component";
import { TripFiltersContext } from "../../context/trip-filters.context";
import {
  coversCovid,
  getLuggageText,
  getTotalPrice,
  isStudentOnly,
  luggageCoverageValue,
  medicalCoverageValue,
  meetsSchengen,
} from "../../utils/coverage.utils";
import { Box, Stack, Typography } from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";

const SORTERS = {
  "price-asc": { label: "Menor preço", value: (item, price) => price(item) },
  "price-desc": { label: "Maior preço", value: (item, price) => -price(item) },
  "medical-desc": {
    label: "Maior cobertura médica",
    value: (item) => -medicalCoverageValue(item.healthCoverage),
  },
  "medical-asc": {
    label: "Menor cobertura médica",
    value: (item) => medicalCoverageValue(item.healthCoverage),
  },
  "luggage-desc": {
    label: "Maior cobertura de bagagem",
    value: (item) => -luggageCoverageValue(getLuggageText(item)),
  },
};

const sameInsurance = (a, b) =>
  a.insuranceCompany === b.insuranceCompany && a.insuranceId === b.insuranceId;

// Escolhe os 3 planos em destaque, sem repetir plano.
//  - Melhor preço: o mais barato (na Europa, o mais barato que atende Schengen).
//  - Nossa escolha: o plano marcado pelo backend (FEATURED_PLANS, em
//    server/service/quotation.service.js); se não houver, o mais barato com
//    pelo menos 60 mil de cobertura médica.
//  - Mais cobertura: o de maior cobertura médica (empate: o mais barato).
// Planos exclusivos para estudantes nunca entram nos destaques.
const pickHighlights = (allResults, isEurope, price) => {
  const results = allResults.filter((item) => !isStudentOnly(item));
  const compliant = isEurope
    ? results.filter((item) => meetsSchengen(item.healthCoverage))
    : results;
  const pool = compliant.length ? compliant : results;
  const byPrice = [...pool].sort((a, b) => price(a) - price(b));
  const chosen = [];
  const isFree = (item) => item && !chosen.some((c) => sameInsurance(c, item));

  const cheapest = byPrice[0];
  if (cheapest) {
    chosen.push(cheapest);
  }

  const featured = pool
    .filter((item) => item.featured)
    .sort((a, b) => (a.featuredRank ?? 0) - (b.featuredRank ?? 0));
  const recommended =
    featured.find(isFree) ||
    byPrice.find(
      (item) =>
        isFree(item) && medicalCoverageValue(item.healthCoverage) >= 60000,
    );
  if (recommended) {
    chosen.push(recommended);
  }

  const mostCoverage = [...byPrice]
    .sort(
      (a, b) =>
        medicalCoverageValue(b.healthCoverage) -
        medicalCoverageValue(a.healthCoverage),
    )
    .find(isFree);
  if (mostCoverage) {
    chosen.push(mostCoverage);
  }

  const highlights = [];
  if (cheapest) {
    highlights.push({
      label: "Melhor preço",
      description:
        isEurope && compliant.length
          ? "O mais barato que atende a exigência da Europa."
          : "O mais barato entre todos os planos.",
      insurance: cheapest,
    });
  }
  if (recommended) {
    highlights.push({
      label: "Nossa escolha",
      description: "O plano que mais recomendamos: ótimo equilíbrio entre preço e cobertura.",
      insurance: recommended,
      featured: true,
    });
  }
  if (mostCoverage) {
    highlights.push({
      label: "Mais cobertura",
      description: "O maior valor para despesas médicas.",
      insurance: mostCoverage,
    });
  }
  return highlights;
};

const FilteredResultList = ({
  resultList,
  loading,
  searching,
  companyStatus,
}) => {
  const { destination, youngPassengers, oldPassengers } =
    useContext(TripFiltersContext);
  const { compareList } = useContext(CompareContext);
  const [sortKey, setSortKey] = useState("price-asc");
  const [filters, setFilters] = useState(null);

  const isEurope = destination === "Europa";
  const price = (item) => getTotalPrice(item, youngPassengers, oldPassengers);

  const results = useMemo(() => resultList ?? [], [resultList]);

  // Destaques só aparecem quando todas as seguradoras responderam,
  // para não trocar de plano enquanto a pessoa está lendo.
  const highlights = useMemo(
    () => (searching ? [] : pickHighlights(results, isEurope, price)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [results, searching, isEurope, youngPassengers, oldPassengers],
  );

  const visibleList = useMemo(() => {
    let list = [...results];

    if (filters) {
      const { healthCoverage, covidCoverage, insuranceCompanies } = filters;
      if (healthCoverage) {
        const minimum = parseFloat(healthCoverage);
        list = list.filter(
          (item) => medicalCoverageValue(item.healthCoverage) >= minimum,
        );
      }
      if (covidCoverage === "true" || covidCoverage === "false") {
        list = list.filter(
          (item) => coversCovid(item) === (covidCoverage === "true"),
        );
      }
      if (insuranceCompanies) {
        list = list.filter((item) =>
          insuranceCompanies.includes(item.insuranceCompany),
        );
      }
    }

    const sortValue = SORTERS[sortKey].value;
    return list.sort(
      (a, b) =>
        sortValue(a, price) - sortValue(b, price) || price(a) - price(b),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, filters, sortKey, youngPassengers, oldPassengers]);

  const orderingMenuOptions = Object.entries(SORTERS).map(
    ([key, { label }]) => ({
      text: label,
      callback: () => setSortKey(key),
    }),
  );

  const handleApplyFilters = (newFilters) => {
    if (newFilters) {
      setFilters(newFilters);
    }
  };

  // Página aberta sem busca (ex: recarregou a página): pede os dados da viagem.
  if (!loading && !searching && !destination && !results.length) {
    return (
      <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
        <Typography variant="h3" sx={{ fontSize: "1.3rem", mb: 1 }}>
          Preencha os dados da sua viagem
        </Typography>
        <Typography sx={{ color: "text.secondary" }}>
          Escolha o destino e as datas acima e clique em Pesquisar para ver os
          planos.
        </Typography>
      </Box>
    );
  }

  return (
    // Espaço extra embaixo quando a barra de comparação está aberta, para ela
    // não cobrir o último plano.
    <Box sx={{ mb: 6, pb: compareList.length ? { xs: 16, md: 10 } : 0 }}>
      {isEurope && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{ mt: 2.5, color: "text.secondary" }}
        >
          <CheckCircleOutline sx={{ color: "success.main", fontSize: 18, mt: "2px" }} />
          <Typography sx={{ fontSize: "0.85rem" }}>
            Europa exige cobertura médica mínima de <b>€30 mil</b> (Schengen). Planos com o
            selo <b>Atende Schengen</b> cumprem a exigência.
          </Typography>
        </Stack>
      )}

      {/* Mesma posição do começo ao fim da busca, para a barra e as frases
          não recomeçarem quando a primeira seguradora responde. */}
      {loading || searching ? (
        <SearchProgress
          companyStatus={companyStatus}
          destination={destination}
        />
      ) : (
        <PlanHighlights highlights={highlights} isEurope={isEurope} />
      )}

      {/* A lista só aparece quando todas as seguradoras responderam, junto com
          os destaques, para nada "pular" na tela enquanto a pessoa lê. */}
      {!loading && !searching && (
        <>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{ mt: 5, mb: 2.5 }}
          >
            <Box>
              <Typography variant="h3" sx={{ fontSize: "1.4rem" }}>
                Todos os planos ({visibleList.length})
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Ordenado por: {SORTERS[sortKey].label.toLowerCase()}
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <FilteringDropMenu
                className="filtering-menu"
                onApply={handleApplyFilters}
              >
                Filtrar por
              </FilteringDropMenu>
              <DropMenu className="ordering-menu" options={orderingMenuOptions}>
                Ordenar por
              </DropMenu>
            </Stack>
          </Stack>

          <Stack spacing={2.5}>
            {visibleList.map((result) => (
              <InsuranceOffer
                key={`${result.insuranceCompany}-${result.insuranceId}`}
                insurance={result}
                isEurope={isEurope}
              />
            ))}
            {visibleList.length === 0 && (
              <Typography
                sx={{ color: "text.secondary", textAlign: "center", py: 4 }}
              >
                {filters
                  ? "Nenhum plano encontrado com esses filtros."
                  : "Nenhuma seguradora retornou planos para essa viagem. Tente outras datas ou pesquise novamente em alguns minutos."}
              </Typography>
            )}
          </Stack>
        </>
      )}
      <CompareTray />
    </Box>
  );
};

export default FilteredResultList;
